import { useEffect, useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  CustomizationThemeDto,
  CreateCustomizationThemeRequest,
  UpdateCustomizationThemeRequest,
} from '@act/shared/data-transfer-objects';
import { useApiCall } from 'redux/utils/api-hook';
import { appSettingsRequests } from 'redux/requests/app-settings.requests';
import { useToast } from 'react-components/app/ReusableToast';
import useUnsavedChangesDialog from '@act/react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { GradientEditor } from './GradientEditor';
import { DndProvider } from 'react-dnd/dist/core/DndProvider';
import { HTML5Backend } from 'react-dnd-html5-backend';

interface CreateEditCustomizationThemeProps {
  theme?: CustomizationThemeDto;
  createMode: boolean;
  onClose: () => void;
  onSuccess: () => void;
  allThemes?: CustomizationThemeDto[];
}

interface CustomizationThemeFormData {
  name: string;
  description: string;
  category: string;
  logoGradientColors: string[];
  accentGradientColors: string[];
  isDeleted: boolean;
}

export const CreateEditCustomizationTheme = ({
  theme,
  createMode,
  onClose,
  onSuccess,
  allThemes = [],
}: CreateEditCustomizationThemeProps) => {
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();

  const [previousLogoColors, setPreviousLogoColors] = useState<string[]>([]);
  const [previousAccentColors, setPreviousAccentColors] = useState<string[]>(
    []
  );

  // Extract unique categories from all themes
  const existingCategories = useMemo(() => {
    const categories = allThemes.map(t => t.category).filter(Boolean);
    return Array.from(new Set(categories)).sort();
  }, [allThemes]);

  const { control, handleSubmit, formState, watch, setValue, reset } =
    useForm<CustomizationThemeFormData>({
      mode: 'onChange',
      defaultValues: {
        name: theme?.name ?? '',
        description: theme?.description ?? '',
        category: theme?.category ?? '',
        logoGradientColors: theme?.logoGradientColors ?? ['#000000'],
        accentGradientColors: theme?.accentGradientColors ?? ['#000000'],
        isDeleted: !!theme?.deletedAt,
      },
    });

  const logoColors = watch('logoGradientColors');
  const accentColors = watch('accentGradientColors');

  const [createResult, isCreating, createTheme] = useApiCall(
    appSettingsRequests.createCustomizationTheme
  );

  const [updateResult, isUpdating, updateTheme] = useApiCall(
    appSettingsRequests.updateCustomizationTheme
  );

  const isLoading = isCreating || isUpdating;
  const isSubmitting = isLoading || formState.isSubmitting;

  useEffect(() => {
    if (createResult && !isCreating) {
      toast('Theme created successfully!', 'success');
      onSuccess();
      onClose();
    }
  }, [createResult, isCreating]);

  useEffect(() => {
    if (updateResult && !isUpdating) {
      toast('Theme updated successfully!', 'success');
      onSuccess();
      onClose();
    }
  }, [updateResult, isUpdating]);

  const onSubmit = async (data: CustomizationThemeFormData) => {
    try {
      const payload:
        | CreateCustomizationThemeRequest
        | UpdateCustomizationThemeRequest = {
        name: data.name,
        description: data.description,
        category: data.category,
        logoGradientColors: data.logoGradientColors,
        accentGradientColors: data.accentGradientColors,
      };

      if (createMode) {
        await createTheme({ body: payload as CreateCustomizationThemeRequest });
      } else {
        const updatePayload: UpdateCustomizationThemeRequest = {
          ...payload,
          deletedAt: data.isDeleted ? Date.now() : null,
        };
        await updateTheme({
          params: { id: theme!.id },
          body: updatePayload,
        });
      }
    } catch (error) {
      toast('An error occurred. Please try again.', 'error');
    }
  };

  const handleClose = () => {
    if (formState.isDirty) {
      void handleUnsavedChanges(() => {
        reset();
        onClose();
      });
    } else {
      reset();
      onClose();
    }
  };

  const handleCopyLogoToAccent = () => {
    setPreviousAccentColors(accentColors);
    setValue('accentGradientColors', [...logoColors], {
      shouldDirty: true,
      shouldValidate: true,
    });
    toast('Copied logo gradient to accent gradient', 'info');
  };

  const handleCopyAccentToLogo = () => {
    setPreviousLogoColors(logoColors);
    setValue('logoGradientColors', [...accentColors], {
      shouldDirty: true,
      shouldValidate: true,
    });
    toast('Copied accent gradient to logo gradient', 'info');
  };

  const handleUndoLogoCopy = () => {
    if (previousLogoColors.length > 0) {
      setValue('logoGradientColors', previousLogoColors, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setPreviousLogoColors([]);
      toast('Undone copy operation', 'info');
    }
  };

  const handleUndoAccentCopy = () => {
    if (previousAccentColors.length > 0) {
      setValue('accentGradientColors', previousAccentColors, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setPreviousAccentColors([]);
      toast('Undone copy operation', 'info');
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 3 }}>
          {/* Basic Information */}
          <Box sx={{ mb: 3 }}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Theme Name"
                  fullWidth
                  required
                  disabled={isSubmitting}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{ mb: 2 }}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Description"
                  fullWidth
                  required
                  multiline
                  rows={2}
                  disabled={isSubmitting}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{ mb: 2 }}
                />
              )}
            />

            <Controller
              name="category"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  {...field}
                  options={existingCategories}
                  freeSolo
                  value={field.value}
                  onChange={(_, newValue) => {
                    field.onChange(newValue || '');
                  }}
                  onInputChange={(_, newInputValue) => {
                    field.onChange(newInputValue);
                  }}
                  disabled={isSubmitting}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Category"
                      required
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        'Select existing or type a new category'
                      }
                    />
                  )}
                />
              )}
            />
          </Box>

          {/* Logo Gradient Editor */}
          <Controller
            name="logoGradientColors"
            control={control}
            render={({ field }) => (
              <Box sx={{ mb: 2 }}>
                <GradientEditor
                  label="Logo Gradient"
                  colors={field.value}
                  onChange={field.onChange}
                  onCopyFrom={handleCopyAccentToLogo}
                  disabled={isSubmitting}
                />
                {previousLogoColors.length > 0 && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleUndoLogoCopy}
                    disabled={isSubmitting}
                    sx={{ mt: -1, mb: 1 }}
                  >
                    Undo Copy
                  </Button>
                )}
              </Box>
            )}
          />

          {/* Accent Gradient Editor */}
          <Controller
            name="accentGradientColors"
            control={control}
            render={({ field }) => (
              <Box sx={{ mb: 2 }}>
                <GradientEditor
                  label="Accent Gradient"
                  colors={field.value}
                  onChange={field.onChange}
                  onCopyFrom={handleCopyLogoToAccent}
                  disabled={isSubmitting}
                />
                {previousAccentColors.length > 0 && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleUndoAccentCopy}
                    disabled={isSubmitting}
                    sx={{ mt: -1, mb: 1 }}
                  >
                    Undo Copy
                  </Button>
                )}
              </Box>
            )}
          />

          {/* Delete Checkbox (Edit Mode Only) */}
          {!createMode && (
            <Box sx={{ mb: 3 }}>
              <Controller
                name="isDeleted"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={isSubmitting}
                      />
                    }
                    label={`${
                      field.value ? 'Mark as deleted' : 'Mark as active'
                    }`}
                  />
                )}
              />
            </Box>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
              pt: 2,
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={isSubmitting || !formState.isValid}
            >
              {isSubmitting
                ? createMode
                  ? 'Creating...'
                  : 'Updating...'
                : createMode
                ? 'Create Theme'
                : 'Update Theme'}
            </Button>
          </Box>
        </Box>
      </form>
    </DndProvider>
  );
};
